import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Instagram, Facebook, Heart, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${API}/business-info`);
        setBusinessInfo(response.data);
      } catch (error) {
        console.error('Error fetching business info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinessInfo();
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      window.history.pushState(null, '', href);
    }
  };

  if (loading || !businessInfo) {
    return (
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Column */}
          <div>
            <img
              src="/logo.png"
              alt="Esther's Elite Braids"
              className="h-20 w-auto object-contain mb-4 bg-white/95 rounded-lg p-2"
              data-testid="footer-logo-img"
            />
            <p className="text-gray-400 mb-4 leading-relaxed">
              Expert African braiding with over 10 years of experience. Protecting and beautifying natural hair.
            </p>
            <div className="flex space-x-4">
              {businessInfo.instagram && (
                <a
                  href={`https://www.instagram.com/${businessInfo.instagram.replace('@', '')}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  data-testid="footer-instagram-link"
                  className="w-10 h-10 bg-amber-800 text-white rounded-full flex items-center justify-center hover:bg-amber-900 transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {businessInfo.facebook && (
                <a
                  href={`https://facebook.com/${businessInfo.facebook.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  data-testid="footer-facebook-link"
                  className="w-10 h-10 bg-amber-800 text-white rounded-full flex items-center justify-center hover:bg-amber-900 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <a href="#home" onClick={(e) => scrollToSection(e, '#home')} className="hover:text-amber-400 transition-colors">
                  Home
                </a>
              </li>
              <li>
                <a href="#services" onClick={(e) => scrollToSection(e, '#services')} className="hover:text-amber-400 transition-colors">
                  Services
                </a>
              </li>
              <li>
                <a href="#gallery" onClick={(e) => scrollToSection(e, '#gallery')} className="hover:text-amber-400 transition-colors">
                  Gallery
                </a>
              </li>
              <li>
                <a href="#pricing" onClick={(e) => scrollToSection(e, '#pricing')} className="hover:text-amber-400 transition-colors">
                  Pricing
                </a>
              </li>
              <li>
                <a href="#about" onClick={(e) => scrollToSection(e, '#about')} className="hover:text-amber-400 transition-colors">
                  About
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4">Our Services</h4>
            <ul className="space-y-2 text-gray-400">
              <li>Box Braids</li>
              <li>Knotless Braids</li>
              <li>Boho Braids</li>
              <li>Cornrows</li>
              <li>Mobile Service Available</li>
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-800">
              <p className="text-sm text-gray-400">
                <span className="font-semibold text-amber-400">Payment:</span> Cash &amp; TWINT
              </p>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <a href={`tel:${businessInfo.phone}`} className="hover:text-amber-400 transition-colors">
                  {businessInfo.phone}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <a href={`mailto:${businessInfo.email}`} className="hover:text-amber-400 transition-colors">
                  {businessInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <span>
                  {businessInfo.address}<br />
                  {businessInfo.city}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} {businessInfo.name}. All rights reserved.
            </p>
            <p className="text-gray-400 text-sm flex items-center">
              Made with <Heart className="w-4 h-4 mx-1 text-amber-400 fill-current" /> for beautiful hair
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
